import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import type { UseCrmSetupConfigurationResult } from "../../setup/hooks/useCrmSetupConfiguration";
import type { CrmSetupConfigurationOption } from "../../setup/types";

export interface UseWhatsAppConfigurationBillingActionsParams {
  channelOption: CrmSetupConfigurationOption | null | undefined;
  setupConfiguration: UseCrmSetupConfigurationResult;
  hasFluxZapExtensionActive?: boolean;
  isFluxZapExtensionLoading?: boolean;
  onFluxmedCheckoutCompleted?: () => void;
  hasConfiguredAutomations?: boolean;
  removeAllAutomationConfigs?: () => Promise<void>;
  /** WABA já persistido no backend (para detectar troca de conta Meta). */
  persistedWabaId?: string;
  onAfterChannelChange?: () => void | Promise<void>;
}

export interface UseWhatsAppConfigurationBillingActionsResult {
  handleFluxmedCheckoutCompleted: () => void;
  isMetaChannel: boolean;
  isFluxmedChannel: boolean;
  isLinkingWhatsAppAccount: boolean;
  canSaveMetaEndpoint: boolean;
  activePlanLabel: string;
  primaryPlanButtonLabel: string;
  planDialogOpen: boolean;
  openPlanDialog: () => void;
  handlePrimaryPlanButtonClick: () => void;
  closePlanDialog: () => void;
  handlePlanDialogClose: (
    _event: object,
    reason?: "backdropClick" | "escapeKeyDown",
  ) => void;
  handleSaveMetaEndpoint: () => Promise<void>;
  metaSaveConfirmOpen: boolean;
  handleConfirmMetaSave: () => Promise<void>;
  handleCancelMetaSave: () => void;
  isMetaChannelChangePending: boolean;
}

export function useWhatsAppConfigurationBillingActions(
  params: UseWhatsAppConfigurationBillingActionsParams,
): UseWhatsAppConfigurationBillingActionsResult {
  const {
    channelOption,
    setupConfiguration,
    onFluxmedCheckoutCompleted,
    hasConfiguredAutomations = false,
    removeAllAutomationConfigs,
    persistedWabaId,
    onAfterChannelChange,
  } = params;
  const hasFluxZapExtensionActive = params.hasFluxZapExtensionActive ?? false;
  const isFluxZapExtensionLoading = params.isFluxZapExtensionLoading ?? false;

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [metaSaveConfirmOpen, setMetaSaveConfirmOpen] = useState(false);
  const lastAutoSaveWabaIdRef = useRef<string | null>(null);
  const metaWabaIdAtDialogOpenRef = useRef<string | null>(null);

  const handleFluxmedCheckoutCompleted = useCallback(() => {
    onFluxmedCheckoutCompleted?.();
  }, [onFluxmedCheckoutCompleted]);

  const isMetaChannel = channelOption === "metaEmbeddedSignup";
  const isFluxmedChannel = channelOption === "fluxmedDefault";
  const isLinkingWhatsAppAccount = channelOption != null;
  const isMetaSelectedInDialog =
    setupConfiguration.configurationStatus?.selectedOption ===
    "metaEmbeddedSignup";
  const hasMetaWabaConnected = Boolean(
    setupConfiguration.configurationStatus?.metaConnection?.wabaId,
  );
  const canSaveMetaEndpoint =
    hasFluxZapExtensionActive &&
    isMetaSelectedInDialog &&
    hasMetaWabaConnected;
  const metaWabaId =
    setupConfiguration.configurationStatus?.metaConnection?.wabaId ?? null;

  const isMetaChannelChangePending = useMemo(() => {
    if (!isMetaSelectedInDialog || !hasMetaWabaConnected) return false;
    if (channelOption !== "metaEmbeddedSignup") return true;

    const nextWabaId = metaWabaId?.trim() ?? "";
    const currentWabaId = persistedWabaId?.trim() ?? "";
    return nextWabaId !== currentWabaId;
  }, [
    channelOption,
    hasMetaWabaConnected,
    isMetaSelectedInDialog,
    metaWabaId,
    persistedWabaId,
  ]);

  const activePlanLabel = useMemo(() => {
    if (isMetaChannel) return "Plano ativo: Meta (WhatsApp Business)";
    if (isFluxmedChannel) return "Plano ativo: Número de telefone padrão Fluxmed";

    return "Plano ativo: não selecionado — use o botão abaixo para escolher Meta ou Fluxmed.";
  }, [isMetaChannel, isFluxmedChannel]);

  const openPlanDialog = useCallback(() => {
    metaWabaIdAtDialogOpenRef.current =
      setupConfiguration.configurationStatus?.metaConnection?.wabaId ?? null;
    setPlanDialogOpen(true);
  }, [setupConfiguration.configurationStatus?.metaConnection?.wabaId]);

  const handlePrimaryPlanButtonClick = useCallback(() => {
    if (isLinkingWhatsAppAccount && isFluxZapExtensionLoading) return;

    openPlanDialog();
  }, [isFluxZapExtensionLoading, isLinkingWhatsAppAccount, openPlanDialog]);

  const closePlanDialog = useCallback(() => {
    setPlanDialogOpen(false);
    setupConfiguration.resetMetaSignupState();
  }, [setupConfiguration]);

  const handlePlanDialogClose = useCallback(
    (_event: object, reason?: "backdropClick" | "escapeKeyDown") => {
      if (reason === "backdropClick") return;
      closePlanDialog();
    },
    [closePlanDialog],
  );

  const executeSaveMetaEndpoint = useCallback(async () => {
    try {
      if (
        isMetaChannelChangePending &&
        hasConfiguredAutomations &&
        removeAllAutomationConfigs
      ) {
        await removeAllAutomationConfigs();
        toast.info(
          "Automações personalizadas removidas. Configure-as novamente quando quiser.",
        );
      }

      await setupConfiguration.saveMetaAccountSelection();
      toast.success("Canal Meta salvo com sucesso.");
      await onAfterChannelChange?.();
      closePlanDialog();
    } catch {
      toast.error(
        "Não foi possível salvar o canal Meta. Tente novamente: reabra o modal e conclua a vinculação com a Meta.",
      );
    } finally {
      window.location.reload();
    }
  }, [
    closePlanDialog,
    hasConfiguredAutomations,
    isMetaChannelChangePending,
    onAfterChannelChange,
    removeAllAutomationConfigs,
    setupConfiguration,
  ]);

  const handleSaveMetaEndpoint = useCallback(async () => {
    if (isMetaChannelChangePending && hasConfiguredAutomations) {
      setMetaSaveConfirmOpen(true);
      return;
    }

    await executeSaveMetaEndpoint();
  }, [
    executeSaveMetaEndpoint,
    hasConfiguredAutomations,
    isMetaChannelChangePending,
  ]);

  const handleConfirmMetaSave = useCallback(async () => {
    setMetaSaveConfirmOpen(false);
    await executeSaveMetaEndpoint();
  }, [executeSaveMetaEndpoint]);

  const handleCancelMetaSave = useCallback(() => {
    setMetaSaveConfirmOpen(false);
  }, []);

  useEffect(() => {
    if (!planDialogOpen) {
      lastAutoSaveWabaIdRef.current = null;
      metaWabaIdAtDialogOpenRef.current = null;
      return;
    }

    if (!canSaveMetaEndpoint) return;
    if (!metaWabaId) return;
    if (setupConfiguration.isSavingMetaAccountSelection) return;

    // Com automações configuradas, exige confirmação explícita no botão Salvar.
    if (hasConfiguredAutomations && isMetaChannelChangePending) return;

    // Auto-save só deve acontecer quando a vinculação Meta foi efetivamente iniciada
    // e concluída durante esta sessão do modal (ex.: após desvincular e re-vincular).
    // Se já existia WABA conectado ao abrir o modal, não auto-salvar nem auto-fechar.
    if (
      metaWabaIdAtDialogOpenRef.current != null &&
      metaWabaIdAtDialogOpenRef.current === metaWabaId
    ) {
      return;
    }

    if (lastAutoSaveWabaIdRef.current === metaWabaId) return;
    lastAutoSaveWabaIdRef.current = metaWabaId;
    void handleSaveMetaEndpoint();
  }, [
    canSaveMetaEndpoint,
    handleSaveMetaEndpoint,
    hasConfiguredAutomations,
    isMetaChannelChangePending,
    metaWabaId,
    planDialogOpen,
    setupConfiguration.isSavingMetaAccountSelection,
  ]);

  const primaryPlanButtonLabel = useMemo(() => {
    if (isMetaChannel) return "Alterar para plano padrão fluxmed";
    if (isFluxmedChannel) return "Alterar para whatsapp business (Meta)";

    return "Ativar plano de mensagens";
  }, [isMetaChannel, isFluxmedChannel]);

  return {
    handleFluxmedCheckoutCompleted,
    isMetaChannel,
    isFluxmedChannel,
    isLinkingWhatsAppAccount,
    canSaveMetaEndpoint,
    activePlanLabel,
    primaryPlanButtonLabel,
    planDialogOpen,
    openPlanDialog,
    handlePrimaryPlanButtonClick,
    closePlanDialog,
    handlePlanDialogClose,
    handleSaveMetaEndpoint,
    metaSaveConfirmOpen,
    handleConfirmMetaSave,
    handleCancelMetaSave,
    isMetaChannelChangePending,
  };
}
